import { SelMessage } from "@selector/shared/components";
import { type FC, useContext } from "react";
import { Card, CardRowSimple, type GridCard, LoadingSpinner } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { GenomicAnnotationsContext } from "@/modules/summary-pages/AnnotationsContext.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

export interface IGenomicAnnotationCardProps extends GridCard {
  sequenced: boolean;
}

const GenomicAnnotationCard: FC<IGenomicAnnotationCardProps> = (props) => {
  const {
    genomicAnnotations: { loading, error, data: genomeAnnotations },
  } = useContext(GenomicAnnotationsContext);

  return (
    <Card key="genome-info-card" title={"Genomic Annotations"}>
      {loading && !genomeAnnotations ? (
        <LoadingSpinner />
      ) : genomeAnnotations == undefined ? (
        // Fallback. Usually graphql errors are handled globally
        <ContentPlaceholder>{error ? error.message : "No data"}</ContentPlaceholder>
      ) : props.sequenced ? (
        <>
          <CardRowSimple label={"Size"} value={formatNumberByThousands(genomeAnnotations.genomeSize) + " bp"} />
          <CardRowSimple label={"Contigs"} value={formatNumberByThousands(genomeAnnotations.contigCount)} />
          <CardRowSimple label={"Number of ORFs"} value={formatNumberByThousands(genomeAnnotations.orfsCount)} />
          <CardRowSimple label={"Annotated genes"} value={formatNumberByThousands(genomeAnnotations.geneCount)} />
          <CardRowSimple
            label={"Annotated transcripts"}
            value={formatNumberByThousands(genomeAnnotations.transcriptsCount)}
          />
          <CardRowSimple
            label={"Annotated proteins"}
            value={formatNumberByThousands(genomeAnnotations.proteinsCount)}
          />
          <CardRowSimple
            label={"E.C. protein assignments"}
            value={formatNumberByThousands(genomeAnnotations.ecProteinAssignmentsCount)}
          />
          <CardRowSimple
            label={"E.C. coverage"}
            value={(genomeAnnotations.ecCoverage ? genomeAnnotations.ecCoverage.toFixed(2) : 0) + "%"}
          />
          <CardRowSimple
            label={"Transcripts linked to genes"}
            value={formatNumberByThousands(genomeAnnotations.genesLinkedTranscriptsCount)}
          />
          <CardRowSimple
            label={"Proteins linked to genes"}
            value={formatNumberByThousands(genomeAnnotations.genesLinkedProteinsCount)}
          />
          <CardRowSimple label={"Genome flags"} value={formatNumberByThousands(genomeAnnotations.flagCount)} />
        </>
      ) : (
        <SelMessage id={"noContent.genomicAnnotations"} />
      )}
    </Card>
  );
};

export { GenomicAnnotationCard };
